import {useState} from 'react';
import {colors} from 'patient-ping-remedy/packages/theme';
import Button, {StyleType} from 'patient-ping-remedy/packages/button';
import Icon, {ICONS} from 'patient-ping-remedy/packages/icon';
import IconButton from 'patient-ping-remedy/packages/icon-button';
import ToolTip from 'patient-ping-remedy/packages/tool-tip';
import ContextMenu from 'patient-ping-remedy/packages/context-menu';
import {StandardSemiBoldTypography} from 'patient-ping-remedy/packages/typography';
import SortableTable, {TableData} from '@common/SortableTable';
import DeleteModal from '@common/DeleteModal';
import {QueueMappingResponse} from '@/api/dto/connect';
import CopyButton from '@/components/common/CopyButton';
import {
  ActionCell,
  QueueIdCell,
  QueueIdText,
} from '@/components/pages/settings/telephony/QueueMappingTable.styles';

type Props = {
  queueMappings: QueueMappingResponse[];
  isLoading: boolean;
  error: Error | null;
  onDelete: (rosterId: number, queueId: string) => void;
  isDeleting: boolean;
  onAddClick: () => void;
  connectWorkspaceUrl?: string;
  onOpenConnect?: () => void;
}

const tableHeaders = ['Group Name', 'Queue Name', 'Queue ID', ''];

function truncateArn(arn: string): string {
  const prefix = 'arn:aws:connect:...';
  const lastSlashIndex = arn.lastIndexOf('/');
  if (lastSlashIndex === -1) return arn;
  const suffix = arn.substring(lastSlashIndex - 5); // "queue/uuid"
  return `${prefix}${suffix}`;
}

export default function QueueMappingTable({
  queueMappings, isLoading, error, onDelete, isDeleting, onAddClick, connectWorkspaceUrl, onOpenConnect,
}: Props) {
  const [deleteTarget, setDeleteTarget] = useState<{
    rosterId: number;
    queueId: string;
    groupName: string;
    queueName: string;
  } | null>(null);

  const tableData: TableData[] = queueMappings.map((mapping) => ({
    groupName: {
      tableHeader: 'Group Name',
      key: 'groupName',
      type: 'string',
      value: mapping.roster.name,
      displayValue: mapping.roster.name,
      columnWidth: '30%',
    },
    queueName: {
      tableHeader: 'Queue Name',
      key: 'queueName',
      type: 'string',
      value: mapping.queueName,
      displayValue: mapping.queueName,
      columnWidth: '30%',
    },
    queueId: {
      tableHeader: 'Queue ID',
      key: 'queueId',
      type: 'string',
      value: mapping.queueId,
      displayValue: (
        <QueueIdCell>
          <ToolTip text={mapping.queueId} position="top" type="dark">
            <QueueIdText>
              {truncateArn(mapping.queueId)}
            </QueueIdText>
          </ToolTip>
          <CopyButton
            value={mapping.queueId}
            ariaLabel="Copy queue ARN"
            errorMessage="Unable to copy queue ARN. Your browser may not support copying."
          />
        </QueueIdCell>
      ),
      columnWidth: '30%',
    },
    actions: {
      tableHeader: '',
      key: 'actions',
      type: 'custom',
      value: '',
      columnWidth: '10%',
      displayValue: (
        <ActionCell>
          <ContextMenu
            menuItems={[
              {
                action: () => setDeleteTarget({
                  rosterId: mapping.roster.id,
                  queueId: mapping.queueId,
                  groupName: mapping.roster.name,
                  queueName: mapping.queueName,
                }),
                label: 'Delete',
              },
            ]}
            position="bottom"
            menuTestId="queue-mapping-context-menu"
            triggerTestId="queue-mapping-context-menu"
          >
            <IconButton iconClass={ICONS['ellipsis-h']} styleType={StyleType.TERTIARY} />
          </ContextMenu>
        </ActionCell>
      ),
    },
  }));

  return (
    <>
      <SortableTable
        id="queue-mappings-table"
        data={tableData}
        loading={isLoading}
        loadError={error}
        header={tableHeaders}
        sortableColumns={tableData.length > 0 ? [
          tableData[0]['groupName'],
          tableData[0]['queueName'],
        ] : []}
        searchColumns={['groupName', 'queueName']}
        searchPlaceholder="Search by group or queue name"
      >
        {connectWorkspaceUrl && onOpenConnect && (
          <Button
            styleType={StyleType.SECONDARY}
            onClick={onOpenConnect}
          >
            <Icon
              iconClass={ICONS.phone}
              style={{ marginRight: '8px' }}
            />
            Open AWS Connect
          </Button>
        )}
        <Button onClick={onAddClick}>
          <Icon iconClass={ICONS.plus} color={colors.white} style={{ marginRight: '10px' }} />
          Add Mapping
        </Button>
      </SortableTable>
      <DeleteModal
        title="queue mapping"
        isOpen={!!deleteTarget}
        close={() => setDeleteTarget(null)}
        confirm={async () => {
          if (deleteTarget) {
            onDelete(deleteTarget.rosterId, deleteTarget.queueId);
          }
        }}
        disabled={isDeleting}
        message={
          <>
            Are you sure you want to remove queue mapping for{' '}
            <StandardSemiBoldTypography>
              {deleteTarget?.groupName}
            </StandardSemiBoldTypography>
            {' → '}
            <StandardSemiBoldTypography>
              {deleteTarget?.queueName}
            </StandardSemiBoldTypography>
            ?
          </>
        }
        warningText={
          'This will disconnect the queue from this group. Existing contact records will not be deleted, '
          + 'but new inbound calls to this queue will no longer be associated with this group.'
        }
      />
    </>
  );
}
